import React, { useState } from 'react';
import {
  useGetIdentity,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { useQuery } from 'react-query';
import { AlertCircle, CheckCircle2, Circle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiFetch } from '../lib/apiFetch';
import { supabase } from '../lib/supabaseClient';
import { Contract, Funeral } from '../types';
import {
  PreneedStep,
  StepAction,
  preneedSteps,
  preneedStatusWord,
} from './preneedTimeline';
import { TrustageCertificateDialog } from './TrustageCertificateDialog';

const PILL: Record<PreneedStep['state'], string> = {
  done: 'bg-emerald-100 text-emerald-800',
  now: 'bg-sky-100 text-sky-800',
  wait: 'bg-amber-100 text-amber-800',
  pending: 'bg-gray-100 text-gray-500',
};

function StepIcon({ state }: { state: PreneedStep['state'] }) {
  if (state === 'done')
    return (
      <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />
    );
  if (state === 'wait')
    return (
      <AlertCircle className="h-5 w-5 text-amber-600" aria-hidden="true" />
    );
  return (
    <Circle
      className={cn(
        'h-5 w-5',
        state === 'now' ? 'text-sky-600' : 'text-gray-300'
      )}
      aria-hidden="true"
    />
  );
}

// Read from `contracts` directly: react-admin maps the `contracts` resource to
// the `contracts_list` view, which doesn't carry the TruStage columns.
export const PRENEED_CONTRACT_SELECT =
  'id, funeral_id, is_preneed, fulfillment_status, trustage_forward, trustage_status, trustage_sent_at, trustage_certificate_number, trustage_certificate_number_set_at, trustage_certificate_number_set_by, trustage_activated_at, trustage_activated_by, certificate_received_at, certificate_document_id, certificate_candidate, confirmation_package_scheduled_at, confirmation_package_held_at, confirmation_package_held_by, confirmation_package_sent_at, confirmation_package_shadow_at';

/** The case's pre-need contract (newest), with the TruStage flow fields. */
export function usePreneedContract(funeralId?: number | string) {
  const { data, refetch } = useQuery(
    ['preneed_contract', funeralId],
    async () => {
      const { data: rows, error } = await supabase
        .from('contracts')
        .select(PRENEED_CONTRACT_SELECT)
        .eq('funeral_id', funeralId)
        .eq('is_preneed', true)
        .order('id', { ascending: false })
        .limit(1);
      if (error) throw error;
      return ((rows as unknown as Contract[]) ?? [])[0] ?? null;
    },
    { enabled: !!funeralId, staleTime: 30 * 1000 }
  );
  return { contract: data ?? null, refetch };
}

/**
 * Pre-need timeline (T-162), inside the Details tab's « Pre-need » section:
 * Quote sent → Contract sent → Signed → Sent to TruStage → Certificate
 * received · Active → Registered → Confirmation package. One row per step,
 * one pill, at most one action.
 */
export function PreneedTimeline() {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [update] = useUpdate();
  const [busy, setBusy] = useState<StepAction | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const { contract, refetch } = usePreneedContract(record?.id);
  const { data: quotes } = useGetList(
    'quotes',
    {
      filter: { funeral_id: record?.id },
      sort: { field: 'created_at', order: 'ASC' },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !!record?.id }
  );

  if (!record) return null;
  const steps = preneedSteps({
    region: record.region,
    quoteSentAt: (quotes?.[0] as any)?.created_at ?? null,
    contractsSentAt: record.contracts_sent_at,
    contractsSignedAt: record.contracts_signed_at,
    registered: !!record.preneed_registry,
    contract,
  });

  const by = identity?.fullName || undefined;
  const post = async (action: StepAction, path: string, done: string) => {
    if (!contract) return;
    setBusy(action);
    try {
      await apiFetch(`preneed/contracts/${contract.id}/${path}`, {
        method: 'POST',
        body: JSON.stringify({ by }),
      });
      notify(done, { type: 'success' });
      void refetch();
      refresh();
    } catch (err: any) {
      notify(err?.message || 'Something went wrong', { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const run = (action: StepAction) => {
    if (action === 'manage_certificate') return setManageOpen(true);
    if (action === 'confirm_certificate')
      return post(
        action,
        'confirm-certificate',
        'Certificate filed: plan Active'
      );
    if (action === 'hold_package')
      return post(action, 'hold-package', 'Confirmation package held');
    if (action === 'send_package')
      return post(action, 'send-package', 'Confirmation package sent');
    if (action === 'toggle_registry') {
      void update(
        'funerals',
        {
          id: record.id,
          data: { ...record, preneed_registry: !record.preneed_registry },
          previousData: record,
        },
        {
          onError: () =>
            notify('Could not update the registry', { type: 'error' }),
        }
      );
    }
  };

  const ACTION_LABEL: Record<StepAction, string> = {
    confirm_certificate: 'Confirm match',
    manage_certificate:
      contract?.trustage_status === 'active' ? 'Edit' : 'Add manually',
    hold_package: 'Cancel / hold',
    send_package: 'Send confirmation package',
    toggle_registry: '',
  };

  return (
    <div className="pt-1" data-testid="preneed-timeline">
      {steps.map((s) => (
        <div key={s.key}>
          <div className="flex justify-between items-center gap-2 py-1.5">
            <span className="text-[13px] text-foreground">{s.label}</span>
            <span className="inline-flex items-center gap-1.5">
              {s.actions?.includes('toggle_registry') ? (
                <input
                  id="preneed-registry-checkbox"
                  type="checkbox"
                  className="h-4 w-4 accent-[hsl(147_37%_29%)]"
                  checked={!!record.preneed_registry}
                  onChange={() => run('toggle_registry')}
                  aria-label="Registered with the Registre des contrats d'arrangements funéraires préalables"
                />
              ) : (
                <StepIcon state={s.state} />
              )}
              <span
                className={cn(
                  'text-[10px] font-medium px-1.5 py-0.5 rounded tabular-nums',
                  PILL[s.state]
                )}
              >
                {s.pill}
              </span>
            </span>
          </div>
          {(s.note || s.actions?.some((a) => a !== 'toggle_registry')) && (
            <div className="mb-1.5 rounded-md border border-border bg-muted/50 px-2.5 py-2 text-[12px] leading-snug text-foreground">
              {s.note && <div>{s.note}</div>}
              {s.actions?.some((a) => a !== 'toggle_registry') && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {s.actions
                    .filter((a) => a !== 'toggle_registry')
                    .map((a) => (
                      <Button
                        key={a}
                        size="sm"
                        variant={
                          a === 'hold_package' ||
                          (a === 'manage_certificate' &&
                            contract?.trustage_status === 'active')
                            ? 'outline'
                            : 'default'
                        }
                        className="h-7 px-2.5 text-xs"
                        disabled={!!busy}
                        onClick={() => void run(a)}
                      >
                        {busy === a ? 'Working…' : ACTION_LABEL[a]}
                      </Button>
                    ))}
                  {s.key === 'certificate' &&
                    s.actions?.includes('confirm_certificate') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2.5 text-xs"
                        disabled={!!busy}
                        onClick={() =>
                          void post(
                            'confirm_certificate',
                            'dismiss-certificate',
                            'Dismissed'
                          )
                        }
                      >
                        Not a certificate
                      </Button>
                    )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {contract && (
        <TrustageCertificateDialog
          contract={contract}
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          onChanged={() => {
            void refetch();
            refresh();
          }}
        />
      )}
    </div>
  );
}

/** "Active" / "At TruStage" next to the section title. */
export function PreneedStatusPill() {
  const record = useRecordContext<Funeral>();
  const { contract } = usePreneedContract(record?.id);
  const word = preneedStatusWord(contract);
  if (!word) return null;
  return (
    <span
      className={cn(
        'ml-2 rounded-md px-2 py-0.5 text-[11px] font-medium',
        word === 'Active'
          ? 'bg-emerald-100 text-emerald-800'
          : 'bg-amber-100 text-amber-800'
      )}
    >
      {word}
    </span>
  );
}
